import React, { useState, useEffect, useMemo } from 'react';
import ClayButton from '@clayui/button';
import Select, { components } from 'react-select';
import './MatchMakingPartnerSelection.css';
import 'react-datepicker/dist/react-datepicker.css';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';

export interface CompanyOption {
    id: string;
    companyName: string;
    industry: string;
    email: string;
}

export interface PartnerSlot {
    id: string;
    dateTime: string;
}

export interface PartnerSelectionRow {
    id: string;
    companyId: string;
    slots: PartnerSlot[];
    companyName?: string;
    industry?: string;
    email?: string;
    status?: string;
}

interface MatchMakingPartnerSelectionProps {
    availableCompanies: CompanyOption[];
    value: PartnerSelectionRow[];
    onChange: (rows: PartnerSelectionRow[]) => void;
    readOnly?: boolean;
}

const MatchMakingPartnerSelection: React.FC<
    MatchMakingPartnerSelectionProps
> = ({ availableCompanies = [], value = [], onChange, readOnly = false }) => {
    // Helper to generate unique IDs
    const generateId = () => Math.random().toString(36).substr(2, 9);
    const navigate = useNavigate();
    const { matchMakingId } = useParams();
    // Ensure at least one row exists initially
    useEffect(() => {
        if (value.length === 0) {
            onChange([
                {
                    id: generateId(),
                    companyId: '',
                    companyName: '',
                    industry: '',
                    email: '',
                    status: 'Details Shared',
                    slots: [
                        { id: generateId(), dateTime: '' },
                        { id: generateId(), dateTime: '' },
                        { id: generateId(), dateTime: '' }
                    ]
                }
            ]);
        }
    }, [value, onChange]);

    const handleAddRow = () => {
        const newRow: PartnerSelectionRow = {
            id: generateId(),
            companyId: '',
            companyName: '',
            industry: '',
            email: '',
            status: 'Details Shared',
            slots: [
                { id: generateId(), dateTime: '' },
                { id: generateId(), dateTime: '' },
                { id: generateId(), dateTime: '' }
            ]
        };
        onChange([...value, newRow]);
    };

    const handleRemoveRow = (rowId: string) => {
        onChange(value.filter((row) => row.id !== rowId));
    };

    const handleCompanyChange = (rowId: string, companyId: string) => {
        const selectedCompany = availableCompanies.find(
            (c) => c.id === companyId
        );

        const newRows = value.map((row) =>
            row.id === rowId
                ? {
                      ...row,
                      companyId,
                      companyName: selectedCompany?.companyName || '',
                      industry: selectedCompany?.industry || '',
                      email: selectedCompany?.email || '',
                      status: 'Details Shared'
                  }
                : row
        );
        onChange(newRows);
    };

    const handleSlotChange = (
        rowId: string,
        slotId: string,
        dateTime: string
    ) => {
        const newRows = value.map((r) => {
            if (r.id === rowId) {
                const newSlots = r.slots.map((s) =>
                    s.id === slotId ? { ...s, dateTime } : s
                );
                return { ...r, slots: newSlots };
            }
            return r;
        });
        onChange(newRows);
    };

    const getRowValidation = (row: PartnerSelectionRow) => {
        const errors: string[] = [];
        if (!row.companyId) errors.push('Company is required');

        const filledSlots = row.slots.filter((s) => s.dateTime);
        if (filledSlots.length === 0)
            errors.push('At least one slot is required');

        // Check uniqueness
        const times = row.slots.map((s) => s.dateTime).filter((t) => t);
        const uniqueTimes = new Set(times);
        if (times.length !== uniqueTimes.size) {
            errors.push('Slots must be unique');
        }

        return errors;
    };

    const preventAll = (e: any) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
    };

    const CustomDropdownIndicator = (props: any) => {
        return (
            <components.DropdownIndicator {...props}>
                <svg
                    width="25"
                    height="25"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M9.99992 11.9838C9.90992 11.9838 9.82714 11.969 9.75158 11.9396C9.67603 11.9101 9.60186 11.859 9.52908 11.7863L5.87158 8.12958C5.79381 8.05181 5.75214 7.95625 5.74658 7.84292C5.74103 7.72958 5.7827 7.62847 5.87158 7.53958C5.96047 7.45069 6.05881 7.40625 6.16658 7.40625C6.27436 7.40625 6.3727 7.45069 6.46158 7.53958L9.99992 11.0779L13.5383 7.53958C13.616 7.46181 13.7116 7.42014 13.8249 7.41458C13.9383 7.40903 14.0394 7.45069 14.1283 7.53958C14.2171 7.62847 14.2616 7.72681 14.2616 7.83458C14.2616 7.94236 14.2171 8.04069 14.1283 8.12958L10.4708 11.7863C10.3985 11.859 10.3244 11.9101 10.2483 11.9396C10.1727 11.969 10.0899 11.9838 9.99992 11.9838Z"
                        fill="#003764"
                    />
                </svg>
            </components.DropdownIndicator>
        );
    };

    const customStyles = {
        control: (provided: any, state: any) => ({
            ...provided,
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: '1px solid #e7e7ed',
            borderRadius: 0,
            boxShadow: 'none',
            minHeight: 'auto',
            '&:hover': {
                borderBottom: '1px solid #e7e7ed'
            }
        }),
        valueContainer: (provided: any) => ({
            ...provided,
            paddingLeft: 0,
            paddingRight: 0
        }),
        singleValue: (provided: any) => ({
            ...provided,
            color: 'var(--primary-color)',
            margin: 0
        }),
        input: (provided: any) => ({
            ...provided,
            color: 'var(--primary-color)',
            margin: 0,
            padding: 0
        }),
        menu: (provided: any) => ({
            ...provided,
            zIndex: 100
        }),
        menuPortal: (base: any) => ({ ...base, zIndex: 100 }),
        dropdownIndicator: (provided: any) => ({
            ...provided,
            color: '#003764',
            ':hover': {
                color: '#003764'
            },
            padding: '4px'
        }),
        indicatorSeparator: (provided: any) => ({
            ...provided,
            display: 'none'
        }),
        option: (provided: any, state: any) => ({
            ...provided,
            backgroundColor: state.isSelected ? 'var(--primary-color)' : '#fff',
            color: state.isSelected ? '#fff' : 'var(--primary-color)',
            ':hover': {
                backgroundColor: 'var(--primary-color)',
                color: '#fff'
            }
        })
    };

    const handleView = (id: number) => {
        navigate(`/application-detail/${matchMakingId}/${id}`);
    };
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="partner-selection-container">
                <div>
                    <table className="table table-autofit show-quick-actions-on-hover table-list table-nowrap table-valign-middle">
                        <thead>
                            <tr>
                                <th className="table-cell-expand">
                                    Company Name
                                </th>
                                <th className="table-cell-expand">Industry</th>
                                {/* {readOnly && (
                                    <th className="table-cell-expand">
                                        Slot Selected By Partner
                                    </th>
                                )} */}
                                <th className="table-cell-expand">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {value.map((row: any) => {
                                const selectedCompany = availableCompanies.find(
                                    (c) => c.id === row.companyId
                                );
                                const otherSelectedIds = value
                                    .filter((r) => r.id !== row.id)
                                    .map((r) => r.companyId)
                                    .filter((id) => id);

                                const rowOptions = availableCompanies.filter(
                                    (c) => !otherSelectedIds.includes(c.id)
                                );

                                return (
                                    <tr key={row.id}>
                                        <td>
                                            {readOnly ? (
                                                <p
                                                    className="m-0"
                                                    style={{
                                                        padding: '8px',
                                                        color: 'var(--primary-color)',
                                                        minHeight: '38px',
                                                        fontSize: '1rem'
                                                    }}
                                                >
                                                    {row.companyName || '-'}
                                                </p>
                                            ) : (
                                                <Select
                                                    aria-label="Select Company"
                                                    value={
                                                        row.companyId
                                                            ? {
                                                                  value: row.companyId,
                                                                  label: row.companyName
                                                              }
                                                            : null
                                                    }
                                                    onChange={(option: any) =>
                                                        handleCompanyChange(
                                                            row.id,
                                                            option?.value
                                                        )
                                                    }
                                                    isDisabled={readOnly}
                                                    options={rowOptions.map(
                                                        (c) => ({
                                                            value: c.id,
                                                            label: c.companyName
                                                        })
                                                    )}
                                                    placeholder="Select Company"
                                                    styles={customStyles}
                                                    isSearchable={true}
                                                    components={{
                                                        DropdownIndicator:
                                                            CustomDropdownIndicator
                                                    }}
                                                    menuPortalTarget={
                                                        document.body
                                                    }
                                                    menuPosition="fixed"
                                                />
                                            )}
                                        </td>
                                        <td
                                            className={
                                                readOnly &&
                                                !row.industry &&
                                                !selectedCompany?.industry
                                                    ? 'text-center'
                                                    : ''
                                            }
                                        >
                                            <span className="partner-industry">
                                                {row.industry ||
                                                    selectedCompany?.industry ||
                                                    (readOnly ? '-' : '')}
                                            </span>
                                        </td>
                                        {/* {readOnly && (
                                            <td className="p-0">
                                                <p
                                                    className="m-0 d-flex align-items-center justify-content-center"
                                                    style={{
                                                        padding: '8px',
                                                        width: '100%',
                                                        color: 'var(--primary-color)',
                                                        fontSize: '1rem',
                                                        minHeight: '38px'
                                                    }}
                                                >
                                                    {row.selectedSlot || '-'}
                                                </p>
                                            </td>
                                        )} */}
                                        <td>
                                            <span>
                                                {row.status || 'Details Shared'}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </LocalizationProvider>
    );
};

export default MatchMakingPartnerSelection;
